import type { IntlShape } from '@redbubble/next/intl';
import logger from '@redbubble/next/logger';
import type { PayPalCheckoutCreatePaymentOptions } from 'braintree-web/paypal-checkout';
import { ErrorCodes, getPaymentErrorMessage } from '../paymentErrorLookup';
import type {
  FlowType,
  Intent,
  PageType,
  PayPalCheckout,
  PayPalErrorCallback,
  PayPalRequiredCart,
  PaypalSdkAddress,
} from '../types';
import {
  subtotalAfterCouponApplied,
  toPaypalLineItems,
  toPaypalShippingOptions,
} from './utils';

export interface HandleCreateOrder {
  cart: PayPalRequiredCart | undefined;
  loggableCart?: string;
  currency: string;
  paypalClient: PayPalCheckout;
  intl: IntlShape;
  onError?: PayPalErrorCallback;
  shippingOverride?: PaypalSdkAddress;
  shippingAddressEditable?: boolean;
  shippingOptionsEditable?: boolean;
  page: PageType;
}

export const handleCreateOrder = async ({
  cart,
  loggableCart,
  currency,
  paypalClient,
  intl,
  onError,
  shippingOverride,
  shippingAddressEditable,
  shippingOptionsEditable,
  page,
}: HandleCreateOrder) => {
  let options: PayPalCheckoutCreatePaymentOptions | undefined;

  try {
    if (!cart) throw new Error('Cart is undefined');

    const {
      summary: cartSummary,
      items: cartLineItems,
      applied_coupon: cartCoupon,
    } = cart;

    const subtotalAfterCoupon = subtotalAfterCouponApplied({
      subtotal: cartSummary.subtotal,
      coupon: cartCoupon,
      intl,
    });

    /* When the discount from a coupon bleeds into shipping, we cannot show shipping options in a dropdown.
     * This is due to complications when offsetting the shipping options amounts by the "bleed amount".
     * For example, if $2 bleeds into the shipping, we can't offset each shipping option by $2 as there is
     * no way to split the $2 into the RB cost and tax.
     *
     * In this case if we did send the shipping options to paypal, it would respond with a HTTP/422
     * because its own calculated grand total compared to the total we send it would be off by $2.
     *
     * Another use case is when we set the shippingOptionsEditable to FALSE, we don't pass shipping options to PayPal,
     * in which case the lineItem amount does not match the order total (total includes shipping), which results in PayPal error.
     * So we add the shipping line as a separate line item.
     */
    const showShippingMethodAsLineItem =
      subtotalAfterCoupon <= 0 || !shippingOptionsEditable;

    const lineItems = toPaypalLineItems({
      cartLineItems,
      intl,
      cartSummary,
      cartCoupon,
      showShippingMethodAsLineItem,
      isCreateOrder: true,
      page,
    });

    const shippingOptions = !showShippingMethodAsLineItem
      ? toPaypalShippingOptions({
          estimatedShipping: cartSummary.estimated_shipping,
          currency,
          intl,
        })
      : undefined;

    /* When we are NOT showing the selected shipping method as a line item, it means that we have passed in the
     * shippingOptions field to the Paypal Modal. For the 'amount' field, we only need send the line
     * items total and the discounts applied to those line items. Paypal will do its own calculations to include
     * the shipping option amount to the total.
     *
     * When we are showing the selected shipping method as a line item, it means that we haven't passed in the
     * shippingOptions field to the Paypal modal. For the 'amount' field, we want to set it to
     * 'cartSummary.total.amount'. This value is inclusive of total line item price, shipping amount and discounts.
     * Since, we're not passing in the shippingOptions, Paypal will not do any calculations to include the
     * selected shipping option amount to the total. So, we have to do it ourselves.
     */
    const createOrderAmount = showShippingMethodAsLineItem
      ? cartSummary.total // inclusive of total line item price, shipping amount, and discounts
      : subtotalAfterCoupon; // inclusive of only total line item price and discounts

    const showReadOnlyShippingAddress =
      !shippingAddressEditable && shippingOverride;

    options = {
      flow: 'checkout' as FlowType,
      intent: 'capture' as Intent,
      amount: createOrderAmount.toFixed(2),
      currency,
      displayName: 'Redbubble',
      enableShippingAddress: true,
      shippingAddressEditable,
      ...(showReadOnlyShippingAddress && {
        shippingAddressOverride: shippingOverride,
      }),
      lineItems,
      shippingOptions,
    };

    const paymentId = await paypalClient.createPayment(options);

    return paymentId;
  } catch (err) {
    const message = `Failed to create PayPal order: ${err.message}`;
    // scrub PII from `options` before logging it to DD
    if (options) options.shippingAddressOverride = undefined;

    // XXX (jj):should this callback be responsible for logging,
    // or should the pages (checkout, cart) be responsible for logging, via onError()?
    logger.error(
      {
        err,
        context: 'PayPalExpress/handlers/handleCreateOrder',
        message,
        cartJson: loggableCart,
        payPalOptions: JSON.stringify(options),
      },
      message
    );
    onError?.(
      getPaymentErrorMessage({ code: ErrorCodes.CHARGE_FAILED_RETRY_ALLOWED })
    );
    throw err;
  }
};
